import { APIResponseType } from 'app/apiService/types';
import apiService from 'app/apiService';

/** One row in the segment grid (new response shape). */
export interface SegmentRow {
  segment_name: string;
  new: { count: number; vins: string[] };
  used: { count: number; vins: string[] };
}

/** Response from GET /preferences/:id - called first when preferences modal opens. */
export interface SegmentInventoriesSummaryResponse {
  _id?: string;
  id?: string;
  properties: { rows: SegmentRow[] };
  scope?: string;
  header_row?: string[];
}

/** Legacy: full vehicle object (for any future detail API). */
export interface SegmentVehicleItem {
  _id: string;
  VIN: string;
  Year: number;
  Make: string;
  Model: string;
  Trim: string;
  Price: number;
  MSRP: number;
  /** When dealer inventory indicates this VIN is certified (CPO). */
  IsCertified?: boolean;
  /** When dealer inventory indicates this VIN has HTML content. */
  IsHtml?: 'ACTIVE' | 'EXPIRED' | 'NOT_AVAILABLE';
  /** When present, indicates whether this vehicle VIN is valid for nurturing. */
  ValidForNurturing?: boolean;
  /** Some backends may return the field in different casing; support it defensively. */
  validForNurturing?: boolean;
  /** Days in lot (can come with different key casing from API). */
  days_in_lot?: number;
  DaysInLot?: number;
  daysInLot?: number;
}

export interface SegmentInventoryItem {
  segment_name: string;
  NEW_VEHICLE: SegmentVehicleItem[];
  USED_VEHICLE: SegmentVehicleItem[];
}

class EmailsGridApiService {
  private static readonly DEALER_INVENTORY_BASE = 'api/v2/dealer-inventory/dealers';
  private static readonly PREFERENCES_BASE = 'api/v2/preferences';

  /** Second path param for template generation preferences. */
  static readonly PREFERENCE_TYPE = 'TEMPLATE_GENERATION_PREFERENCES';

  /**
   * GET /preferences/:id/TEMPLATE_GENERATION_PREFERENCE — call first when preferences modal opens.
   * Authorization: Bearer token (via interceptors).
   * Returns properties.rows with segment counts for the grid.
   */
  static async getPreferences(
    dealerId: string,
  ): Promise<APIResponseType<SegmentInventoriesSummaryResponse>> {
    if (!dealerId) {
      return {
        body: null as any,
        message: 'Dealer ID is required',
      };
    }
    const url = `${EmailsGridApiService.PREFERENCES_BASE}/${dealerId}/${EmailsGridApiService.PREFERENCE_TYPE}`;
    return apiService.get<SegmentInventoriesSummaryResponse>(url);
  }

  /**
   * PUT /preferences/:preference_id — update preferences (e.g. after user changes selected VINs).
   * Body shape same as GET response: { properties: { rows } }.
   */
  static async updatePreferences(
    preferenceId: string,
    body: SegmentInventoriesSummaryResponse,
  ): Promise<APIResponseType<SegmentInventoriesSummaryResponse>> {
    if (!preferenceId) {
      return {
        body: null as any,
        message: 'Preference ID is required',
      };
    }
    const url = `${EmailsGridApiService.PREFERENCES_BASE}/${preferenceId}`;
    return apiService.put<SegmentInventoriesSummaryResponse>(url, body, undefined, false);
  }

  /**
   * GET .../segment-inventories?segment=&scope= (optional, for full vehicle details).
   * Use when backend supports fetching vehicle details by segment+scope.
   */
  static async getSegmentInventoryBySegmentAndScope(
    dealerId: string,
    segmentName: string,
    scope: 'NEW_VEHICLE' | 'USED_VEHICLE',
  ): Promise<APIResponseType<SegmentInventoryItem[]>> {
    if (!dealerId || !segmentName) {
      return { body: [], message: 'Dealer ID and segment are required' };
    }
    const url = `${EmailsGridApiService.DEALER_INVENTORY_BASE}/${dealerId}/segment-inventories`;
    return apiService.get<SegmentInventoryItem[]>(url, {
      params: { segment: segmentName, scope },
    });
  }
}

export default EmailsGridApiService;
